import {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  MouseEvent,
  useMemo,
} from "react";
import clsx from "clsx";
import MingcuteDownFill from "src/VirtualOMS/assets/icons/MingcuteDownFill";
import { DropdownProps, SelectOptionType } from "src/VirtualOMS/types/common";

interface IProps extends DropdownProps {
  options: SelectOptionType[];
}

export default function SelectComponent({
  floating,
  title,
  placeholder,
  options,
  selectedOption,
  onSelectedOptionChange,
  onIsOpenChange,
}: IProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const maximumCharactersCount = useMemo(() => {
    return Math.max(
      placeholder?.length ?? 0,
      ...options.map((option) => option.name.length)
    );
  }, [placeholder, options]);

  const selectOption = useCallback(
    (option: SelectOptionType): void => {
      if (option !== selectedOption) {
        onSelectedOptionChange?.(option);
      }
    },
    [onSelectedOptionChange, selectedOption]
  );

  const optionClickHandler = (
    e: MouseEvent<HTMLLIElement>,
    option: SelectOptionType
  ): void => {
    e.stopPropagation();
    selectOption(option);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    }
    onIsOpenChange?.(isOpen);
  }, [isOpen, onIsOpenChange]);

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    const keydownHandler = (e: KeyboardEvent): void => {
      if (e.target !== containerRef.current) {
        return;
      }

      switch (e.code) {
        case "Enter":
        case "Space": {
          e.preventDefault();
          if (isOpen) {
            selectOption(options[highlightedIndex]);
          }
          setIsOpen((prev) => !prev);
          break;
        }
        case "ArrowUp":
        case "ArrowDown": {
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            break;
          }
          const newValue = highlightedIndex + (e.code === "ArrowDown" ? 1 : -1);
          if (newValue >= 0 && newValue < options.length) {
            setHighlightedIndex(newValue);
          }
          break;
        }
        case "Escape": {
          e.preventDefault();
          setIsOpen(false);
          break;
        }
      }
    };

    containerElement.addEventListener("keydown", keydownHandler);
    return () => {
      containerElement.removeEventListener("keydown", keydownHandler);
    };
  }, [isOpen, highlightedIndex, options, selectOption]);

  return (
    <div
      ref={containerRef}
      onBlur={() => setIsOpen(false)}
      onClick={() => setIsOpen((old) => !old)}
      tabIndex={0}
      className={clsx(
        "w-full relative flex items-center gap-2 px-4 py-2 border rounded-md outline-none cursor-default",
        isOpen ? "border-blue-500" : "border-gray-300",
        floating && "bg-gray-700 shadow-lg",
        floating && !isOpen && !containerRef.current?.matches(":focus")
          ? "border-transparent"
          : ""
      )}
    >
      {title && <span className="text-sm font-bold">{title}: </span>}
      <span
        className="flex-1"
        style={{
          minWidth: `${maximumCharactersCount}ch`,
        }}
      >
        {selectedOption?.name ?? placeholder ?? "\u00A0"}
      </span>
      <div
        className={clsx("text-sm", isOpen ? "text-blue-500" : "text-gray-500")}
      >
        <MingcuteDownFill />
      </div>
      <ul
        className={clsx(
          "absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto z-50 max-h-60 min-w-full w-max",
          !isOpen && "hidden"
        )}
      >
        {options.map((option, index) => (
          <li
            key={option.id}
            className={clsx(
              "px-2 py-1 cursor-pointer",
              option === selectedOption && "bg-blue-500 text-white",
              index === highlightedIndex && "bg-slate-300"
            )}
            onMouseEnter={() => setHighlightedIndex(index)}
            onClick={(e) => optionClickHandler(e, option)}
          >
            {option.name}
          </li>
        ))}
      </ul>
    </div>
  );
}
